import { MdChevronLeft } from "react-icons/md";
import { MdChevronRight } from "react-icons/md";
import { scrollToElement } from "../../utils/utils";
import Button from "../Buttons/Button";

type PaginationProps<T> = {
  data: T[];
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  scrollTargetId?: string;
  scrollDuration?: number;
};

const Pagination = <T,>({
  data,
  totalPages,
  currentPage,
  setCurrentPage,
  scrollTargetId = '',
  scrollDuration = 1000,
}: PaginationProps<T>) => {
  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
    scrollToElement(scrollTargetId, -50, scrollDuration)
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
    scrollToElement(scrollTargetId, -50, scrollDuration)
  };

  const handleSelectPage = (page: number) => {
    if (typeof page === "number") {
      setCurrentPage(page);
      scrollToElement(scrollTargetId, -50, scrollDuration)
    } else {
      return null;
    }
  };

  const getPaginationRange = (): (number | string)[] => {
    const range: (number | string)[] = [];
    const showPages = 1; // Números visibles al inicio y al final
    const surroundingPages = 1; // Números visibles alrededor de la actual

    if (totalPages <= showPages + surroundingPages * 2 + 2) {
      // Mostrar todas las páginas si son pocas
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
    } else {
      // Agregar los primeros números
      for (let i = 1; i <= showPages; i++) {
        range.push(i);
      }

      // Agregar "..." si hay un salto
      if (currentPage > showPages + surroundingPages) {
        range.push("...");
      }

      // Agregar páginas alrededor de la actual
      for (
        let i = Math.max(currentPage - surroundingPages, showPages + 1);
        i <= Math.min(currentPage + surroundingPages, totalPages - showPages);
        i++
      ) {
        range.push(i);
      }

      // Agregar "..." antes de los últimos números
      if (currentPage + surroundingPages < totalPages - showPages) {
        range.push("...");
      }

      // Agregar los últimos números
      for (let i = totalPages - showPages + 1; i <= totalPages; i++) {
        range.push(i);
      }
    }

    return range;
  };

  const paginationRange = getPaginationRange();

  return (
    <>
      {data?.length > 0 ? (
        <div className="mb-6 w-full flex items-center justify-between md:justify-center mt-4">
          <div className="flex w-full  items-center justify-between md:justify-center gap-1">
            <Button
              title="Página anterior"
              label=""
              className='page-item next flex items-center justify-center !h-[2.5rem] !w-[2.5rem] mr-4 !p-0'
              onClick={() => handlePreviousPage()}
              disabled={currentPage === 1}
              icon={MdChevronLeft}
              iconSize={24}
              variant="outline-green"
              showIconRight={true}
            />
            <div className="flex gap-1">
              {/* Rango de paginación */}
              {paginationRange.map((page, index) => (
                page === "..." ? (
                  <span key={index} className="py-2 px-4 text-tmedium text-brand-green-15">
                    ...
                  </span>
                ) : (
                  <button
                    key={index}
                    className={`rounded-sm py-2 px-4  ${page === currentPage
                      ? "text-tmedium bg-brand-green-30 text-primary-100"
                      : "text-tmedium text-brand-green-15 transition-all duration-300 hover:bg-brand-green-5"
                      }`}
                    onClick={() => handleSelectPage(Number(page))}
                  >
                    {page}
                  </button>
                )))}
            </div>
            <Button
              title="Página siguiente"
              label=""
              className='page-item next flex items-center justify-center !h-[2.5rem] !w-[2.5rem] ml-4 !p-0'
              onClick={() => handleNextPage()}
              disabled={currentPage === totalPages}
              icon={MdChevronRight}
              iconSize={24}
              variant="outline-green"
              showIconRight={true}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Pagination;